import React, {useContext, useEffect, useRef, useState} from 'react';
import {FaBars, FaFileAlt, FaFolder, FaPlus, FaFolderPlus, FaTrash, FaEdit, FaChevronLeft} from 'react-icons/fa';
import {Tree} from 'react-arborist';
import {useApplicationEvents, useEventListener} from "../../../infrastructure/events/useBroadcastChannel";
import useResizeObserver from "use-resize-observer";
import {useSelectSong} from "../../navigation/components/Navigation";
import {AppCtx} from "../../../AppCtx";
import "./FileBrowserComponent.css";
import VerticalQuickMenuComponent from "../../menu/components/VerticalQuickMenuComponent";
import {
  EditorFileChangedEvent, FileBrowserFileSelectedEvent, FileDeletedEvent, FileRenamedEvent,
  FileSavedToDatabaseEvent, UIRefreshFilesEvent
} from "../../../infrastructure/events/channels";
import {listAllFiles} from "../../song/services/songs-db";
import { useNavigate } from 'react-router-dom';
import {catalogUrl} from '../../dashboard/component/dashboardData';

// Import services
import { buildTree, closeAllNodes, countLeaves } from '../services/treeBuilder';
import { createFile, createFolder, renameItem, deleteFile } from '../services/fileOperations';
import { isValidDrop, processDrop } from '../services/dragDropHandler';
import yjsDocumentService from "../../../infrastructure/sync/YjsDocumentService";
import {getWorkspaceId} from "../../../shared/services/dbUtils";
import { sanitizeFileName, sanitizeFolderName } from "../../../shared/services/utils";
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';

export const FILE_BROWSER_OVERLAY_QUERY = '(max-width: 640px)';

const useFileBrowserOverlay = () => {
  const [overlay, setOverlay] = useState(() => window.matchMedia(FILE_BROWSER_OVERLAY_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia(FILE_BROWSER_OVERLAY_QUERY);
    const update = () => setOverlay(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return overlay;
};

export const FileBrowserSidebarComponent = ({ children }) => {
  const isReadOnly = useWorkspaceReadOnly();
  const [files, setFiles] = useState([]);
  const [virtualFolders, setVirtualFolders] = useState([]);
  const [data, setData] = useState([]);
  const selectSong = useSelectSong();
  const {isPresentationMode, isFileBrowserVisible, setIsFileBrowserVisible, isTopMenuVisible, selectedFile, setSelectedFile, currentSessionId, isCommonSessionEnabled, showQuickMenu, setShowQuickMenu} = useContext(AppCtx);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, node: null });
  const [renameDialog, setRenameDialog] = useState({ visible: false, node: null, newName: '' });
  const [newFileDialog, setNewFileDialog] = useState({ visible: false, parentPath: '', fileName: '', content: '' });
  const [newFolderDialog, setNewFolderDialog] = useState({ visible: false, parentPath: '', folderName: '' });
  const [draggedNode, setDraggedNode] = useState(null);
  const [showPeekButton, setShowPeekButton] = useState(false);
  const peekButtonTimerRef = useRef(null);
  const openButtonRef = useRef(null); // Ref for the open button itself
  const closeButtonRef = useRef(null);
  const wasOverlayOpen = useRef(false);
  const overlay = useFileBrowserOverlay();
  const navigate = useNavigate();

  const fileDeletedEvent = useApplicationEvents(FileDeletedEvent);
  const fileRenamedEvent = useApplicationEvents(FileRenamedEvent);
  const editorFileChanged = useApplicationEvents(EditorFileChangedEvent);
  const fileSavedToDatabase = useApplicationEvents(FileSavedToDatabaseEvent);

  // Helper function to extract source and target nodes from events
  const getNodes = (e) => {
    let s = e.dragNode || e.item || draggedNode || null;
    let t = e.dropTarget || e.target || null;
    return { sourceNode: s, targetNode: t };
  };

  const fetchFiles = async () => {
    try {
      const allFiles = await listAllFiles();
      if (JSON.stringify(allFiles) !== JSON.stringify(files)) {
        setFiles(allFiles);
        setData(buildTree(allFiles, virtualFolders));
      }
    } catch (error) {}
  };

  useEffect(() => {
    setData(buildTree(files, virtualFolders));
  }, [files, virtualFolders]);

  useEffect(() => {
    fetchFiles();
    const refreshInterval = setInterval(() => fetchFiles(), 30000);
    return () => clearInterval(refreshInterval);
  }, []);

  const closeOverlay = () => {
    if (overlay) setIsFileBrowserVisible(false);
  };

  const nodeClk = async (node) => {
    if (!node.isLeaf) {
      node.toggle();
      navigate(catalogUrl(node.data.path));
    } else {
       if (node.data.path !== undefined) {
        selectSong(node.data.path);
        closeOverlay();
       }
    }
  };

  const cMenu = (event, node) => {
    event.preventDefault();
    event.stopPropagation();
    if (isReadOnly) return;
    setContextMenu({visible: true, x: event.clientX, y: event.clientY, node: node});
  };

  const closeContextMenu = () => setContextMenu({ visible: false, x: 0, y: 0, node: null });

  const addFile = (parentPath = '') => {
    setNewFileDialog({visible: true, parentPath: parentPath, fileName: '', content: ''});
    closeContextMenu();
  };

  const addFolder = (parentPath = '') => {
    setNewFolderDialog({visible: true, parentPath: parentPath, folderName: ''});
    closeContextMenu();
  };

  const delFile = (node) => {
    if (window.confirm(`Are you sure you want to delete "${node.data.path}"?`)) {
      deleteFile(
        { path: node.data.path },
        selectedFile,
        setSelectedFile,
        navigate,
        fileDeletedEvent
      );
    }
    closeContextMenu();
  };

  const doRenameFile = (node) => {
    // For files, remove .txt extension from the displayed name for easier editing
    let displayName = node.data.name;
    if (node.isLeaf && displayName.endsWith('.txt')) {
      displayName = displayName.slice(0, -4); // Remove .txt extension
    }
    
    setRenameDialog({visible: true, node: node, newName: displayName});
    closeContextMenu();
  };

  const renameSubmit = () => {
    if (!renameDialog.newName || renameDialog.newName === renameDialog.node.data.name) {
      setRenameDialog({ visible: false, node: null, newName: '' });
      return;
    }

    renameItem(
      {
        oldPath: renameDialog.node.data.path,
        newName: renameDialog.newName,
        isFolder: !renameDialog.node.isLeaf
      },
      selectedFile,
      setSelectedFile,
      editorFileChanged,
      fileRenamedEvent
    );

    setRenameDialog({ visible: false, node: null, newName: '' });
  }

  // Handle input change with sanitization
  const handleFileNameChange = (e) => {
    const sanitized = sanitizeFileName(e.target.value);
    setNewFileDialog({...newFileDialog, fileName: sanitized});
  };

  const handleFolderNameChange = (e) => {
    const sanitized = sanitizeFolderName(e.target.value);
    setNewFolderDialog({...newFolderDialog, folderName: sanitized});
  };

  const handleRenameChange = (e) => {
    const sanitized = sanitizeFileName(e.target.value);
    setRenameDialog({...renameDialog, newName: sanitized});
  };

  const doCreateFile = () => {
    if (!newFileDialog.fileName) return;

    createFile(
      {
        parentPath: newFileDialog.parentPath,
        fileName: newFileDialog.fileName,
        content: newFileDialog.content
      },
      setSelectedFile,
      editorFileChanged,
      fileSavedToDatabase
    );

    setNewFileDialog({ visible: false, parentPath: '', fileName: '', content: '' });
    fetchFiles();
    selectSong(
      newFileDialog.parentPath
        ? `${newFileDialog.parentPath}/${newFileDialog.fileName}${!newFileDialog.fileName.endsWith('.txt') ? '.txt' : ''}`
        : `${newFileDialog.fileName}${!newFileDialog.fileName.endsWith('.txt') ? '.txt' : ''}`
    );
    closeOverlay();
  };

  const doCreateFolder = () => {
    if (!newFolderDialog.folderName) return;

    const fullPath = createFolder({
      parentPath: newFolderDialog.parentPath,
      folderName: newFolderDialog.folderName
    });

    if (fullPath) {
      setVirtualFolders(prev => [...prev, fullPath]);
      setNewFolderDialog({ visible: false, parentPath: '', folderName: '' });

      setTimeout(() => {
        if (treeRef.current) {
          const parentNode = newFolderDialog.parentPath ? treeRef.current.get(newFolderDialog.parentPath) : null;
          if (parentNode && !parentNode.isOpen) parentNode.open();
        }
      }, 100);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        const contextMenuElement = document.querySelector(".context-menu");
        if (contextMenuElement && !contextMenuElement.contains(event.target)) {
          closeContextMenu();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  // Effect to handle showing/hiding the peek button when sidebar is hidden
  useEffect(() => {
    const handleDocumentClick = (event) => {
      // Don't show the peek button if we're clicking on the button itself
      if (openButtonRef.current && !openButtonRef.current.contains(event.target)) {
        setShowPeekButton(true);
        setShowQuickMenu(true);
        
        // Clear any existing timer
        if (peekButtonTimerRef.current) {
          clearTimeout(peekButtonTimerRef.current);
        }
        
        // Hide the button after a delay
        peekButtonTimerRef.current = setTimeout(() => {
          setShowPeekButton(false);
          setShowQuickMenu(false);
        }, 2500); // Hide after 2.5 seconds
      }
    };

    if (!isFileBrowserVisible) {
      document.addEventListener('click', handleDocumentClick);
    } else {
      // When sidebar becomes visible or presentation mode is activated, hide the button and clear timers
      setShowPeekButton(false);
      setShowQuickMenu(false);
      if (peekButtonTimerRef.current) {
        clearTimeout(peekButtonTimerRef.current);
      }
    }

    // Cleanup function
    return () => {
      document.removeEventListener('click', handleDocumentClick);
      if (peekButtonTimerRef.current) {
        clearTimeout(peekButtonTimerRef.current);
      }
    };
  }, [isFileBrowserVisible]); // Re-run this effect when sidebar visibility changes

  useEffect(() => {
    if (!overlay || !isFileBrowserVisible) return;
    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setIsFileBrowserVisible(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [overlay, isFileBrowserVisible, setIsFileBrowserVisible]);

  useEffect(() => {
    if (overlay && isFileBrowserVisible) {
      wasOverlayOpen.current = true;
      closeButtonRef.current?.focus();
      return;
    }
    if (wasOverlayOpen.current) {
      wasOverlayOpen.current = false;
      document.getElementById('file-browser-toggle')?.focus();
    }
  }, [overlay, isFileBrowserVisible]);

  // Subscribe to file list updates
  useEffect(() => {
    return yjsDocumentService.subscribeToFileList(getWorkspaceId(), (fileList) => {
      setFiles(fileList);
    });
  }, [currentSessionId]);

  useEventListener(FileBrowserFileSelectedEvent, (event) => {
    if (isCommonSessionEnabled && event.__source === 'ws') {
      if (treeRef.current) {
        closeAllNodes(treeRef.current.root);
        const node = treeRef.current.select(event.fileName);
        if (node) node.select();
      }
    }
  });

  useEventListener(UIRefreshFilesEvent, (event) => {
    fetchFiles();

    if (event.action === 'delete' && selectedFile && selectedFile.name === event.fileName) {
      setSelectedFile({name: null, content: null, found: false});
      navigate('/');
    }

    if (event.action === 'rename' && selectedFile && (selectedFile.name === event.oldFileName || selectedFile.name === (event.newFileName || event.fileName))) {
      const updatedFile = {...selectedFile, name: event.newFileName || event.fileName};
      setSelectedFile(updatedFile);
      navigate(catalogUrl(updatedFile.name) + window.location.hash, {replace: true});
    }
  });

  // Drag and drop handlers
  const dragStart = (event) => {
    if ((event.source && event.source.isLeaf) || (event.target && event.target.isLeaf)) {
      setDraggedNode(event.source || event.target);
    } else if (event.preventDefault) {
      event.preventDefault();
    }
  };

  const dragEnd = () => {
    // Could clear the dragged node state here if needed
  };

  const handleMove = (args) => {
    if (isReadOnly) return false;
    try {
      const { dragIds, parentId } = args;

      if (!dragIds?.length || !parentId) return false;

      const sourceNode = treeRef.current?.get(dragIds[0]);
      const targetNode = treeRef.current?.get(parentId);

      if (!sourceNode || !targetNode || !isValidDrop(sourceNode, targetNode)) return false;

      processDrop(sourceNode, targetNode, {
        selectedFile, setSelectedFile, navigate, editorFileChanged, fileRenamedEvent
      });

      setTimeout(() => fetchFiles(), 500);
      return false; // Let the tree component handle the UI update
    } catch (error) {
      return false;
    }
  };

  const handleDrop = (event) => {
    if (isReadOnly) return false;
    try {
      const { sourceNode, targetNode } = getNodes(event);
      if (!sourceNode || !targetNode) return false;

      return processDrop(sourceNode, targetNode, {
        selectedFile, setSelectedFile, navigate, editorFileChanged, fileRenamedEvent
      });
    } catch (error) {
      return false;
    }
  };

  const canDrop = (event) => {
    if (isReadOnly) return false;
    const { sourceNode, targetNode } = getNodes(event);
    if (!sourceNode || !targetNode) return false;
    return isValidDrop(sourceNode, targetNode);
  };

  // Tree node renderer
  function NodeRenderer({node, dragHandle}) {
    const indent = node.level * 7;
    const isSelected = node.isSelected;
    const isOpen = node.isOpen;
    const isBeingDragged = node.state?.dragging;
    const isDropTarget = node.state?.dropTarget;
    
    // Display name without .txt extension for leaf nodes (files)
    let displayName = node.data.name;
    if (node.isLeaf && displayName.endsWith('.txt')) {
      displayName = displayName.slice(0, -4); // Remove .txt extension
    }
    
    return (
      <div style={{
        paddingLeft: `${indent}px`,
        cursor: 'pointer',
        backgroundColor: isDropTarget ? 'rgba(30, 144, 255, 0.2)' : 'transparent',
        opacity: isBeingDragged ? 0.5 : 1
      }}
        className={`tree-node ${isDropTarget ? 'drop-target' : ''}`}
        onClick={() => nodeClk(node)}
        onContextMenu={(e) => cMenu(e, node)}
        ref={!isReadOnly && node.isLeaf ? dragHandle : undefined}>
        <nobr>
          <span>
            {node.isLeaf
              ? <FaFileAlt style={{color: `var(${isSelected ? '--file-icon-selected' : '--file-icon-default'})`}}/>
              : <FaFolder style={{color: `var(${isOpen ? '--folder-icon-open' : '--folder-icon-closed'})`}}/>}
            <span style={{paddingLeft: "3px", textDecoration: isSelected ? 'underline' : 'none', color: 'var(--sidebar-text)'}}>
              {displayName}
              {!node.isLeaf && <span style={{fontSize: "11px", paddingLeft: "2px", color: 'var(--text-muted)'}}>({countLeaves(node)})</span>}
            </span>
          </span>
        </nobr>
      </div>
    );
  }

  // Dialog button helper components
  const DialogButton = ({ onClick, className, disabled, children }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`dialog-button ${className}`}
      disabled={disabled}
    >
      {children}
    </button>
  );

  const CancelButton = ({ onClick }) => (
    <DialogButton onClick={onClick} className="dialog-button-cancel">Cancel</DialogButton>
  );

  // UI Rendering
  const treeRef = useRef();
  const treeOpenState = useRef({sessionId: currentSessionId, value: {}});
  if (treeOpenState.current.sessionId !== currentSessionId) treeOpenState.current = {sessionId: currentSessionId, value: {}};
  const {ref: panelRef, width: panelWidth = 1, height: panelHeight = 1} = useResizeObserver();
  const {ref: menuRef, width: menuWidth = 1, height: menuHeight = 1} = useResizeObserver();
  const treeHeight = Math.max(0, panelHeight - menuHeight - 2);

  return (
    <>
      {overlay && isFileBrowserVisible && (
        <div className="file-browser-backdrop" onClick={() => setIsFileBrowserVisible(false)} />
      )}
      <div
        ref={panelRef}
        className={`file-browser${overlay ? ' file-browser--overlay' : ''}`}
        style={{display: isFileBrowserVisible ? 'block' : 'none'}}
        role={overlay ? 'dialog' : undefined}
        aria-modal={overlay ? true : undefined}
        aria-label="Panel plików"
        aria-hidden={!isFileBrowserVisible}
      >
        <div ref={menuRef} className="file-browser-menu">{isFileBrowserVisible && children}</div>
        <div
          className="file-browser-tree"
          style={{ height: treeHeight }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu({visible: true, x: e.clientX, y: e.clientY, node: null});
          }}>
          {isFileBrowserVisible && <Tree
            ref={treeRef}
            data={data}
            width={panelWidth}
            height={treeHeight}
            openByDefault={false}
            initialOpenState={treeOpenState.current.value}
            selection={selectedFile?.name || undefined}
            onToggle={() => {treeOpenState.current.value = {...treeRef.current?.openState};}}
            disableDrag={isReadOnly}
            disableDrop={isReadOnly}
            onDrop={handleDrop}
            onDragStart={dragStart}
            onDragEnd={dragEnd}
            onMove={handleMove}
            canDrop={canDrop}
            dndOptions={{
              dragGraphics: "preview",
              enablePointerEventsOnDrop: true,
              monitor: true,
              enableHoverClass: true,
              dropTargetOffset: 5,
              dragLeafNodesOnly: true,
              drag: {delay: 100, touch: 500}
            }}
            indent={10}
          >
            {NodeRenderer}
          </Tree>}
        </div>
        
        {/* Sidebar Close Button */}
        <button 
          ref={closeButtonRef}
          className="sidebar-close-button" 
          onClick={() => setIsFileBrowserVisible(false)}
          title="Zamknij panel plików"
          aria-label="Zamknij panel plików"
          aria-expanded="true"
        >
          <FaChevronLeft />
        </button>

      {/* Context Menu */}
      {!isReadOnly && contextMenu.visible && (
        <div className="context-menu" style={{top: contextMenu.y, left: contextMenu.x}} onClick={(e) => e.stopPropagation()}>
          <div className="context-menu-item" onClick={(e) => {
            e.stopPropagation();
            addFile(contextMenu.node ? contextMenu.node.data.path : '');
          }} title="Utwórz nowy plik">
            <FaPlus style={{ marginRight: '8px' }} />
            {contextMenu.node && !contextMenu.node.isLeaf ? 'Add File Here' : 'Add File'}
          </div>

          <div className="context-menu-item" onClick={(e) => {
            e.stopPropagation();
            addFolder(contextMenu.node ? contextMenu.node.data.path : '');
          }} title="Utwórz nowy folder">
            <FaFolderPlus style={{ marginRight: '8px' }} />
            {contextMenu.node && !contextMenu.node.isLeaf ? 'Add Subfolder' : 'Add Folder'}
          </div>

          {contextMenu.node && contextMenu.node.isLeaf && (
            <>
              <div className="context-menu-separator" />
              <div className="context-menu-item" onClick={(e) => {
                e.stopPropagation();
                doRenameFile(contextMenu.node);
              }} title="Zmień nazwę pliku">
                <FaEdit style={{ marginRight: '8px' }} /> Rename
              </div>
              <div className="context-menu-item danger" onClick={(e) => {
                e.stopPropagation();
                delFile(contextMenu.node);
              }} title="Usuń plik">
                <FaTrash style={{ marginRight: '8px' }} /> Delete
              </div>
            </>
          )}
        </div>
      )}

      {/* Dialogs */}
      {!isReadOnly && renameDialog.visible && (
        <div className="dialog-base rename-dialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="dialog-title">
            Rename {!renameDialog.node?.isLeaf ? 'Folder' : 'File'}
          </h3>
          <input
            type="text"
            value={renameDialog.newName}
            onChange={handleRenameChange}
            className="dialog-input"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameDialog.newName) {
                e.preventDefault();
                renameSubmit();
              }
            }}
          />
          <div className="dialog-button-container">
            <CancelButton onClick={() => setRenameDialog({visible: false, node: null, newName: ''})} />
            <DialogButton
              onClick={renameSubmit}
              className="dialog-button-primary"
            >
              Rename
            </DialogButton>
          </div>
        </div>
      )}

      {!isReadOnly && newFileDialog.visible && (
        <div className="dialog-base new-file-dialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="dialog-title">Create New File</h3>
          {newFileDialog.parentPath && (
            <div className="dialog-location">
              <strong>Location:</strong> {newFileDialog.parentPath}
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="fileName" className="dialog-label">
              File Name
            </label>
            <input
              id="fileName"
              type="text"
              value={newFileDialog.fileName}
              onChange={handleFileNameChange}
              className="dialog-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFileDialog.fileName) {
                  e.preventDefault();
                  doCreateFile();
                }
              }}
            />
          </div>
          <div className="dialog-button-container">
            <CancelButton onClick={() => setNewFileDialog({visible: false, parentPath: '', fileName: '', content: ''})} />
            <DialogButton
              onClick={doCreateFile}
              className="dialog-button-success"
              disabled={!newFileDialog.fileName}
            >
              Create File
            </DialogButton>
          </div>
        </div>
      )}

      {!isReadOnly && newFolderDialog.visible && (
        <div className="dialog-base new-folder-dialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="dialog-title">Create New Folder</h3>
          {newFolderDialog.parentPath && (
            <div className="dialog-location">
              <strong>Location:</strong> {newFolderDialog.parentPath}
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="folderName" className="dialog-label">Folder Name</label>
            <input
              id="folderName"
              type="text"
              value={newFolderDialog.folderName}
              onChange={handleFolderNameChange}
              className="dialog-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFolderDialog.folderName) {
                  e.preventDefault();
                  doCreateFolder();
                }
              }}
            />
          </div>
          <div className="dialog-button-container">
            <CancelButton onClick={() => setNewFolderDialog({visible: false, parentPath: '', folderName: ''})} />
            <DialogButton
              onClick={doCreateFolder}
              className="dialog-button-success"
              disabled={!newFolderDialog.folderName}
            >
              Create Folder
            </DialogButton>
          </div>
        </div>
      )}
    </div>

    {/* Peek Sidebar Button - only shown when sidebar is hidden and user clicks on screen */}
    {!isFileBrowserVisible && (
      <>
        <button
          ref={openButtonRef} // Assign ref to the button
          className={`sidebar-open-button ${showPeekButton ? 'visible' : ''}`}
          onClick={(e) => {
            e.stopPropagation(); // Prevent the document click handler from firing
            setIsFileBrowserVisible(true);
          }}
          title="Otwórz panel plików"
          aria-label="Otwórz panel plików"
          aria-expanded="false"
        >
          <FaChevronLeft />
        </button>
        
        {/* The quick menu with buttons defined at the App level */}
        {/* The component takes care of visibility state */}
        <VerticalQuickMenuComponent visible={showQuickMenu} />
      </>
    )}
    </>
  );
};

export const FileBrowserButtonComponent = () => {
  const {isFileBrowserVisible, setIsFileBrowserVisible} = useContext(AppCtx);
  const browserResized = useApplicationEvents('FileBrowserResizedEvent');
  const toggleBrowserVisibility = () => {
    setIsFileBrowserVisible(!isFileBrowserVisible);
    setTimeout(() => browserResized.postMessage({}), 200);
  };
  return (
    <div>
      <div onClick={toggleBrowserVisibility} 
           id="file-browser-toggle"
           style={{cursor: 'pointer', borderRadius: '30%', padding: '5px'}}
           role="button" tabIndex={0}
           aria-expanded={isFileBrowserVisible}
           aria-label={isFileBrowserVisible ? "Ukryj panel plików" : "Pokaż panel plików"}
           title={isFileBrowserVisible ? "Ukryj panel plików" : "Pokaż panel plików"}>
        <FaBars style={{color: 'var(--menu-icon-color)'}}/>
      </div>
    </div>
  );
};
